import { useEffect, useRef, useState } from "react";
import BtnArrow from "@/components/ui/btn-arrow";
import { BtnSpinner } from "@/components/ui/btn-spinner";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import FileUploader, {
  UploadedFile,
  activeUploadProgress,
  uploadRefs,
} from "@/components/forms/FileUploader";
import { snapshotJourney } from "@/lib/journey";
import { useUnloadWarning } from "@/hooks/useUnloadWarning";


/**
 * Stepped project-evaluation form used inside the ConsultationModal popup.
 * Distinct from `ConsultationForm.tsx` (single-scroll page form on /contact).
 *
 * Flow:
 *   Step 1 · Starting Point   (Architectural Lighting Design | Project Review)
 *   Step 2 · Project           (type, location, stage, name, brief description)
 *   Step 3 · Path questions    (branches on selected path)
 *   Step 4 · About You         (name, company, email, phone, role) + submit
 *
 * Preserves existing Supabase edge-function submissions
 * (`send-tour-request` + `send-form-submission`) and attachment encoding.
 */

export type ConsultationPath = "design" | "value_analysis";

const ROLES = ["Architect", "Developer", "Owner", "Contractor", "Engineer", "Other"] as const;

import { FORM_PROJECT_TYPES as PROJECT_TYPES } from "@/data/projectTypes";

const DESIGN_PHASES = [
  "Early planning / pre-design",
  "Design in progress",
  "Drawings issued, pre-construction",
  "Out for bid, pre-construction",
  "Under construction",
] as const;

const REVIEW_STAGES = [
  "Design development",
  "Construction documents",
  "Issued for bid",
  "Pre-construction",
  "Procurement",
  "Under construction",
] as const;

const DESIGN_PRIORITIES = [
  "Architecture and atmosphere",
  "Visual comfort and glare",
  "Controls and scenes",
  "Energy and maintenance",
  "Budget alignment",
  "Schedule and coordination",
  "Not sure yet",
] as const;

const DESIGN_RISK_FLAGS = [
  "Budget pressure",
  "Schedule pressure",
  "Controls scope unclear",
  "Design intent at risk",
  "Coordination gaps",
  "None yet",
] as const;

const VA_PRESSURES = [
  "Cost",
  "Lead time",
  "Availability",
  "Performance",
  "Controls compatibility",
  "Constructability",
  "Substitutions",
  "Maintenance",
] as const;

/* ------------------------------- Styling ------------------------------ */
const inputCls =
  "w-full min-h-[44px] bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors font-light";
const triggerCls =
  "w-full min-h-[44px] bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-3 text-sm text-white/70 data-[placeholder]:text-white/40 focus:ring-0 focus:ring-offset-0 focus:border-white/60 transition-colors h-auto font-light";
const itemCls =
  "font-light text-white/85 focus:bg-white/10 focus:text-white data-[highlighted]:bg-white/10 data-[highlighted]:text-white data-[state=checked]:text-white";
const chipBase =
  "px-4 py-2 min-h-[36px] text-xs font-light tracking-[0.05em] border transition-all duration-300";
const chipInactive = "border-white/15 text-white/70 hover:border-white/40 hover:text-white";
const chipActive = "border-white bg-white/[0.06] text-white";
const eyebrowCls = "text-white/45 font-sans font-light text-[10px] tracking-[0.25em] uppercase";

/* ------------------------------ Schemas ------------------------------ */
const projectSchema = z.object({
  projectType: z.string().min(1, "Select a project type"),
  projectLocation: z.string().trim().min(1, "Add a location").max(200),
  projectPhase: z.string().min(1, "Select the current stage"),
  projectName: z.string().trim().max(200).optional().or(z.literal("")),
});
const aboutSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  company: z.string().trim().min(1, "Company is required").max(100),
  email: z.string().trim().email("Enter a valid email").max(255),
  phone: z.string().trim().max(50).optional().or(z.literal("")),
  role: z.enum(ROLES, { errorMap: () => ({ message: "Select your role" }) }),
});

/* ------------------------------ Component ------------------------------ */
interface Props {
  /** Preselect a starting path. When omitted, opens on the picker (step 1). */
  defaultPath?: ConsultationPath;
  /** Called when the user chooses the codelumenLabs link under the picker. */
  onLabsClick?: () => void;
  /** Reset counter — bumped by parent to reset form. */
  resetKey?: number;
}

type FieldKey =
  | "projectType"
  | "projectLocation"
  | "projectPhase"
  | "name"
  | "company"
  | "email"
  | "role";

const SteppedConsultationForm = ({ defaultPath, onLabsClick, resetKey }: Props) => {
  const [path, setPath] = useState<ConsultationPath | null>(defaultPath ?? null);
  const [step, setStep] = useState<number>(1);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [intake, setIntake] = useState({
    projectType: "",
    projectLocation: "",
    projectPhase: "",
    projectName: "",
    message: "",
    designPriorities: [] as string[],
    designRisks: [] as string[],
    hasFloorPlan: "" as "" | "yes" | "no" | "unsure",
    hasElectricalPlans: "" as "" | "yes" | "no" | "unsure",
    hasLightingLayoutAndSchedule: "" as "" | "both" | "layout_only" | "schedule_only" | "no" | "unsure",
    vaConfirmByOthers: false,
    vaPressures: [] as string[],
    vaPrompt: "",
    name: "",
    company: "",
    email: "",
    phone: "",
    role: "",
  });
  const [pathSwitchedFromVA, setPathSwitchedFromVA] = useState(false);
  const [vaOverrideKeepPath, setVaOverrideKeepPath] = useState(false);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const uploadPct = activeUploadProgress(files);
  const uploadsPending = files.some((f) => f.status === "uploading" || f.status === "queued");
  const uploadsFailed = files.some((f) => f.status === "failed");
  const [hp, setHp] = useState("");
  const startedAtRef = useRef<number>(Date.now());
  const stepTopRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Reset when parent bumps resetKey (e.g. modal closed & reopened).
    setPath(defaultPath ?? null);
    setStep(1);
    setErrors({});
    setSuccess(false);
    setFiles([]);
    setIntake({
      projectType: "",
      projectLocation: "",
      projectPhase: "",
      projectName: "",
      message: "",
      designPriorities: [],
      designRisks: [],
      hasFloorPlan: "",
      hasElectricalPlans: "",
      hasLightingLayoutAndSchedule: "",
      vaConfirmByOthers: false,
      vaPressures: [],
      vaPrompt: "",
      name: "",
      company: "",
      email: "",
      phone: "",
      role: "",
    });
    setPathSwitchedFromVA(false);
    setVaOverrideKeepPath(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    stepTopRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [step]);

  const isVA = path === "value_analysis";
  const totalSteps = 4;
  const stepLabels = ["Starting Point", "Project", isVA ? "Review" : "Priorities", "About You"];

  const toggle = <K extends "designPriorities" | "designRisks" | "vaPressures">(
    key: K,
    value: string,
  ) =>
    setIntake((prev) => {
      const arr = prev[key];
      return {
        ...prev,
        [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });

  const setField = (key: FieldKey, value: string) => {
    setIntake((p) => ({ ...p, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  useUnloadWarning(uploadsPending);

  const goNext = () => {
    // Never advance while any file is still being uploaded or has failed.
    if (uploadsPending) {
      toast.error("Uploads in progress", { description: "Please wait for files to finish uploading." });
      return;
    }
    if (uploadsFailed) {
      toast.error("Some uploads failed", { description: "Retry or remove failed files before continuing." });
      return;
    }
    if (step === 1) {
      if (!path) {
        toast.error("Choose a starting point");
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      const parsed = projectSchema.safeParse({
        projectType: intake.projectType,
        projectLocation: intake.projectLocation,
        projectPhase: intake.projectPhase,
        projectName: intake.projectName,
      });
      if (!parsed.success) {
        const next: Partial<Record<FieldKey, string>> = {};
        for (const issue of parsed.error.issues) {
          const k = issue.path[0] as FieldKey | undefined;
          if (k && !next[k]) next[k] = issue.message;
        }
        setErrors(next);
        toast.error("Please complete the project details");
        return;
      }
      setStep(3);
      return;
    }
    if (step === 3) {
      if (isVA && !intake.vaConfirmByOthers) {
        toast.error("Please confirm the lighting design was prepared by others");
        return;
      }
      setStep(4);
      return;
    }
  };

  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = aboutSchema.safeParse({
      name: intake.name,
      company: intake.company,
      email: intake.email,
      phone: intake.phone,
      role: intake.role,
    });
    if (!parsed.success) {
      const next: Partial<Record<FieldKey, string>> = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as FieldKey | undefined;
        if (k && !next[k]) next[k] = issue.message;
      }
      setErrors(next);
      toast.error("Please complete your details");
      return;
    }

    if (uploadsPending) {
      toast.error("Upload still in progress", { description: "Please wait for uploads to finish." });
      return;
    }
    if (uploadsFailed) {
      toast.error("Some uploads failed", { description: "Please retry or remove failed files." });
      return;
    }

    setSubmitting(true);
    try {
      const uploads = uploadRefs(files);
      const journey = snapshotJourney();



      const summaryParts = [
        `Path: ${isVA ? "Project Review" : "Architectural Lighting Design"}`,
        `Type: ${intake.projectType}`,
        `Location: ${intake.projectLocation}`,
        `Stage: ${intake.projectPhase}`,
        intake.projectName ? `Project: ${intake.projectName}` : null,
        isVA
          ? `Primary pressure: ${intake.vaPressures.join(", ") || "—"}`
          : `Priorities: ${intake.designPriorities.join(", ") || "—"}`,
        !isVA && intake.designRisks.length ? `Risks: ${intake.designRisks.join(", ")}` : null,
        isVA && intake.vaPrompt ? `What prompted the review: ${intake.vaPrompt}` : null,
        uploads.length ? `Attachments: ${uploads.map((u) => u.filename).join(", ")}` : null,
        intake.message ? `Notes: ${intake.message}` : null,
      ].filter(Boolean);

      const d = parsed.data;

      // Architectural / Project Review submissions route ONLY through
      // send-form-submission (CRM + branded customer confirmation). Do not
      // also invoke send-tour-request here — that endpoint sends a Labs
      // confirmation and would cause the user to receive a wrong/duplicate
      // email. Labs bookings use LabsBookingForm which calls send-tour-request
      // exclusively.
      const crmRes = await supabase.functions.invoke("send-form-submission", {
        body: {
          full_name: d.name,
          email: d.email,
          company: d.company,
          phone: d.phone || undefined,
          message: intake.message,
          source_form: isVA ? "value_analysis" : "lighting_design",
          details: {
            path: isVA ? "value_analysis" : "lighting_design",
            role: d.role,
            project_name: intake.projectName || undefined,
            project_location: intake.projectLocation || undefined,
            project_type: intake.projectType || undefined,
            project_phase: intake.projectPhase || undefined,
            design_priorities: intake.designPriorities,
            design_risks: intake.designRisks,
            va_confirm_by_others: intake.vaConfirmByOthers || undefined,
            va_pressures: intake.vaPressures,
            va_prompt: intake.vaPrompt || undefined,
            drawings_available: {
              floor_plan: intake.hasFloorPlan || undefined,
              electrical_plans: intake.hasElectricalPlans || undefined,
              lighting_layout_and_schedule: intake.hasLightingLayoutAndSchedule || undefined,
            },
            path_switched_from_va: pathSwitchedFromVA || undefined,
          },
          uploads,
          hp,
          started_ms: Date.now() - startedAtRef.current,
          journey,
        },
      });

      if (crmRes.error) throw crmRes.error;


      setSuccess(true);
      toast.success("Request received");
    } catch {
      toast.error("Submission failed", {
        description: "Please try again or email info@clumen.com.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------------- Success screen --------------------------- */
  if (success) {
    return (
      <div className="border border-white/15 p-8 md:p-10">
        <p className={`${eyebrowCls} mb-4`}>
          {isVA ? "Review requested" : "Project received"}
        </p>
        <h2 className="font-sans font-light text-white text-xl md:text-2xl tracking-[-0.01em] leading-snug">
          {isVA
            ? "Your project is with our design team."
            : "Your project information has been received."}
        </h2>
        <p className="mt-4 text-white/65 font-sans font-light text-sm leading-relaxed">
          {isVA
            ? "A senior codelumen team member will review the information and confirm the next step within one business day."
            : "A senior codelumen team member will review it and respond within one business day."}
        </p>
      </div>
    );
  }

  /* --------------------------- Progress indicator --------------------------- */
  const Progress = (
    <div ref={stepTopRef} className="flex items-center justify-between gap-4 mb-5">
      <p className={eyebrowCls}>
        Step {step} of {totalSteps} · {stepLabels[step - 1]}
      </p>
      <div className="flex gap-1.5" aria-hidden>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <span
            key={i}
            className={`h-[2px] w-8 transition-colors ${
              i < step ? "bg-white/80" : "bg-white/15"
            }`}
          />
        ))}
      </div>
    </div>
  );

  /* --------------------------------- Steps --------------------------------- */
  return (
    <form onSubmit={submit} className="space-y-4 form-mobile-pad" noValidate>
      {/* Honeypot — real users leave empty; bots typically autofill */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        aria-hidden="true"
        className="sr-only"
      />
      {Progress}

      {/* Step 1 · Starting Point */}
      {step === 1 && (
        <div className="space-y-4">
          <p className={eyebrowCls}>01 · Choose your starting point</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(
              [
                {
                  key: "design" as const,
                  title: "Architectural Lighting Design",
                  desc: "For projects that need a complete lighting strategy from concept through commissioning.",
                  note: "Independent professional design service. Clients are not required to purchase the lighting package through codelumen.",
                },
                {
                  key: "value_analysis" as const,
                  title: "Project Review",
                  desc: "For projects already in motion, with drawings, a lighting layout, or a fixture schedule in hand.",
                  note: "codelumen reviews design intent, integration, controls, and constructability, then tells you where the installed result is at risk and what it would take to hold it.",
                },
              ]
            ).map((opt) => {
              const active = path === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setPath(opt.key)}
                  aria-pressed={active}
                  className={`relative text-left p-5 md:p-6 border transition-all duration-300 min-h-[44px] ${
                    active
                      ? "border-white bg-white/[0.06]"
                      : "border-white/25 hover:border-white/60"
                  }`}
                >
                  {active && (
                    <span
                      aria-hidden
                      className="absolute left-0 top-0 bottom-0 w-[2px] bg-white"
                    />
                  )}
                  <p className="font-sans font-light text-white text-base tracking-[-0.005em]">
                    {opt.title}
                  </p>
                  <p className="mt-2 text-white/60 font-sans font-light text-xs leading-relaxed">
                    {opt.desc}
                  </p>
                  <p className="mt-3 text-white/40 font-sans font-light text-[11px] italic leading-relaxed">
                    {opt.note}
                  </p>
                </button>
              );
            })}
          </div>
          <p className="pt-2 text-white/50 font-sans font-light text-xs leading-relaxed">
            Looking to experience codelumenLabs?{" "}
            <button
              type="button"
              onClick={onLabsClick}
              className="text-white/85 hover:text-white underline underline-offset-4 transition-colors"
            >
              Request an in-person or virtual presentation.
            </button>
          </p>
          <StepNav
            onBack={null}
            onNext={goNext}
            nextLabel="Next"
            nextDisabled={!path}
          />
        </div>
      )}

      {/* Step 2 · Project */}
      {step === 2 && (
        <div className="space-y-4">
          <p className={eyebrowCls}>
            02 · Project
            <span className="opacity-60 ml-2">
              · {isVA ? "Project Review" : "Architectural Lighting Design"}
            </span>
          </p>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
            <FieldWrap error={errors.projectType}>
              <Select
                value={intake.projectType}
                onValueChange={(v) => setField("projectType", v)}
              >
                <SelectTrigger aria-label="Project type" className={triggerCls}>
                  <SelectValue placeholder="Project type" />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border border-white/10 z-[60]">
                  {PROJECT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} className={itemCls}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldWrap>
            <FieldWrap error={errors.projectLocation}>
              <input
                type="text"
                placeholder="Project location, city / country"
                value={intake.projectLocation}
                onChange={(e) => setField("projectLocation", e.target.value)}
                maxLength={200}
                className={inputCls}
              />
            </FieldWrap>
            <FieldWrap error={errors.projectPhase}>
              <Select
                value={intake.projectPhase}
                onValueChange={(v) => setField("projectPhase", v)}
              >
                <SelectTrigger aria-label="Project status" className={triggerCls}>
                  <SelectValue placeholder="Project status" />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border border-white/10 z-[60]">
                  {DESIGN_PHASES.map((p) => (
                    <SelectItem key={p} value={p} className={itemCls}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldWrap>
            <FieldWrap>
              <input
                type="text"
                placeholder="Project name (optional)"
                value={intake.projectName}
                onChange={(e) => setIntake((p) => ({ ...p, projectName: e.target.value }))}
                maxLength={200}
                className={inputCls}
              />
            </FieldWrap>
          </div>
          <StepNav onBack={goBack} onNext={goNext} nextLabel="Next" />
        </div>
      )}

      {/* Step 3 · Path questions */}
      {step === 3 && !isVA && (
        <div className="space-y-5">
          <div>
            <p className={eyebrowCls}>03 · What matters most (optional)</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {DESIGN_PRIORITIES.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => toggle("designPriorities", v)}
                  className={`${chipBase} ${
                    intake.designPriorities.includes(v) ? chipActive : chipInactive
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <DrawingsIntake intake={intake} setIntake={setIntake} />

          <div>
            <p className={eyebrowCls}>
              {intake.hasFloorPlan === "no" ? "References (optional)" : "Project files"}
            </p>
            <p className="mt-2 mb-3 text-white/55 font-sans font-light text-xs leading-relaxed">
              {designAttachHint(intake)} Up to 10 files, 300 MB each.
            </p>
            <FileUploader
              source="lighting_design"
              files={files}
              onChange={setFiles}
              variant="dark"
              helperText={
                intake.hasFloorPlan === "no"
                  ? "Optional. Reference imagery, site photos, or a short brief."
                  : "Upload plans, RCPs, specs, or reference imagery"
              }
            />
          </div>

          <StepNav
            onBack={goBack}
            onNext={goNext}
            nextLabel={uploadsPending ? `Uploading files… ${uploadPct ?? 0}%` : "Next"}
            nextDisabled={uploadsPending || uploadsFailed}
          />
        </div>
      )}

      {step === 3 && isVA && (
        <div className="space-y-5">
          <div className="border border-white/10 bg-white/[0.03] p-4 md:p-5">
            <p className="text-white/70 font-sans font-light text-xs md:text-sm leading-relaxed">
              A project review examines an existing lighting design against the architecture it
              serves. Design authorship stays with its author.
            </p>
          </div>

          <DrawingsIntake intake={intake} setIntake={setIntake} />

          {vaRecommendsDesignSwitch(intake) && !vaOverrideKeepPath && (
            <div className="border border-white/25 bg-white/[0.04] p-5 md:p-6">
              <p className={eyebrowCls}>A better starting point</p>
              <p className="mt-3 font-sans font-light text-white text-base md:text-lg tracking-[-0.005em] leading-snug">
                It sounds like your project is a great fit for Architectural Lighting Design.
              </p>
              <p className="mt-3 text-white/65 font-sans font-light text-xs md:text-sm leading-relaxed">
                A project review reads a lighting design that is already drawn, a plan with a lighting layout and a fixture schedule. Based on what you shared, your project is earlier in that journey, which is exactly where our Architectural Lighting Design team adds the most. We develop the layout, the schedule, and the controls strategy with you, and can review the set later once it is complete.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setPath("design");
                    setPathSwitchedFromVA(true);
                  }}
                  className="group btn-primary-light min-h-[44px]"
                >
                  <span className="btn-label">
                    Continue as Architectural Lighting Design <BtnArrow />
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setVaOverrideKeepPath(true)}
                  className="text-white/60 hover:text-white text-xs tracking-[0.15em] uppercase underline underline-offset-4"
                >
                  Keep exploring a Project Review
                </button>
              </div>
            </div>
          )}

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={intake.vaConfirmByOthers}
              onChange={(e) =>
                setIntake((p) => ({ ...p, vaConfirmByOthers: e.target.checked }))
              }
              className="mt-1 h-4 w-4 accent-white bg-transparent"
            />
            <span className="text-white/80 font-sans font-light text-sm leading-relaxed">
              I confirm the existing lighting design was prepared by others.
            </span>
          </label>

          <div>
            <p className={eyebrowCls}>Project stage</p>
            <div className="mt-4">
              <Select
                value={intake.projectPhase}
                onValueChange={(v) => setField("projectPhase", v)}
              >
                <SelectTrigger aria-label="Project stage" className={triggerCls}>
                  <SelectValue placeholder="Project stage" />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border border-white/10 z-[60]">
                  {REVIEW_STAGES.map((p) => (
                    <SelectItem key={p} value={p} className={itemCls}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <p className={eyebrowCls}>Primary pressure (optional)</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {VA_PRESSURES.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => toggle("vaPressures", v)}
                  className={`${chipBase} ${
                    intake.vaPressures.includes(v) ? chipActive : chipInactive
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className={eyebrowCls}>What prompted the review?</p>
            <div className="mt-3">
              <Textarea
                rows={2}
                placeholder="A sentence or two is enough. Optional."
                value={intake.vaPrompt}
                onChange={(e) =>
                  setIntake((p) => ({ ...p, vaPrompt: e.target.value.slice(0, 500) }))
                }
                className={`${inputCls} resize-none`}
              />
            </div>
          </div>

          <div>
            <p className={eyebrowCls}>Project documents</p>
            <p className="mt-2 mb-3 text-white/55 font-sans font-light text-xs leading-relaxed">
              {vaRecommendsDesignSwitch(intake) && vaOverrideKeepPath
                ? "Attach whatever you have."
                : "Drawings, specifications, fixture schedule, or renderings."}{" "}
              Up to 10 files, 300 MB each.
            </p>
            <FileUploader
              source={isVA ? "value_analysis" : "lighting_design"}
              files={files}
              onChange={setFiles}
              variant="dark"
              helperText="Upload drawings, specs, or fixture schedule"
            />
          </div>


          <StepNav
            onBack={goBack}
            onNext={goNext}
            nextLabel={uploadsPending ? `Uploading files… ${uploadPct ?? 0}%` : "Next"}
            nextDisabled={uploadsPending || uploadsFailed}
          />
        </div>
      )}

      {/* Step 4 · About You */}
      {step === 4 && (
        <div className="space-y-4">
          <p className={eyebrowCls}>04 · About You</p>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
            <FieldWrap error={errors.name}>
              <input
                type="text"
                placeholder="Name"
                value={intake.name}
                onChange={(e) => setField("name", e.target.value)}
                maxLength={100}
                autoComplete="name"
                className={inputCls}
              />
            </FieldWrap>
            <FieldWrap error={errors.company}>
              <input
                type="text"
                placeholder="Company"
                value={intake.company}
                onChange={(e) => setField("company", e.target.value)}
                maxLength={100}
                autoComplete="organization"
                className={inputCls}
              />
            </FieldWrap>
            <FieldWrap error={errors.email}>
              <input
                type="email"
                placeholder="Email"
                value={intake.email}
                onChange={(e) => setField("email", e.target.value)}
                maxLength={255}
                autoComplete="email"
                className={inputCls}
              />
            </FieldWrap>
            <FieldWrap>
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={intake.phone}
                onChange={(e) => setIntake((p) => ({ ...p, phone: e.target.value.slice(0, 50) }))}
                autoComplete="tel"
                className={inputCls}
              />
            </FieldWrap>
            <div className="md:col-span-2">
              <FieldWrap error={errors.role}>
                <Select value={intake.role} onValueChange={(v) => setField("role", v)}>
                  <SelectTrigger aria-label="Your role" className={triggerCls}>
                    <SelectValue placeholder="Your role" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border border-white/10 z-[60]">
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r} className={itemCls}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldWrap>
            </div>
          </div>


          <div className="form-action-bar justify-between">
            <button
              type="button"
              onClick={goBack}
              className="text-white/70 hover:text-white text-xs tracking-[0.15em] uppercase min-h-[44px] px-2"
            >
              ← Back
            </button>
            <button
              type="submit"
              disabled={submitting || uploadPct !== null}
              aria-busy={submitting}
              data-loading={submitting || undefined}
              className="group btn-primary-light justify-center min-h-[48px] flex-1 max-w-[70%]"
            >
              <span className="btn-label">
                {uploadPct !== null
                  ? `Uploading… ${uploadPct}%`
                  : submitting
                    ? "Submitting…"
                    : (
                      <>
                        {isVA ? "Submit Request" : "Submit Request"}{" "}
                        <BtnArrow />
                      </>
                    )}
              </span>
              <BtnSpinner
                label={isVA ? "Submitting request" : "Submitting request"}
              />
            </button>
          </div>
          <p className="text-white/40 font-sans font-light text-[11px] leading-relaxed text-center">
            A senior team member will respond within one business day.
          </p>

        </div>
      )}
    </form>
  );
};

/* ----------------------------- helpers ----------------------------- */

const FieldWrap = ({
  children,
  error,
}: {
  children: React.ReactNode;
  error?: string;
}) => (
  <div>
    {children}
    {error && (
      <p className="mt-2 text-destructive text-[11px] font-light" role="alert">
        {error}
      </p>
    )}
  </div>
);

const StepNav = ({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
}: {
  onBack: (() => void) | null;
  onNext: () => void;
  nextLabel: string;
  nextDisabled?: boolean;
}) => (
  <div className="form-action-bar justify-between">
    {onBack ? (
      <button
        type="button"
        onClick={onBack}
        className="text-white/70 hover:text-white text-xs tracking-[0.15em] uppercase min-h-[44px] px-2"
      >
        ← Back
      </button>
    ) : (
      <span />
    )}
    <button
      type="button"
      onClick={onNext}
      disabled={nextDisabled}
      className="group btn-primary-light justify-center min-h-[48px] flex-1 max-w-[70%] disabled:opacity-50"
    >
      <span className="btn-label">
        {nextLabel} <BtnArrow />
      </span>
    </button>
  </div>
);


/* ----------------------- Drawings-availability intake ----------------------- */

type IntakeShape = {
  hasFloorPlan: "" | "yes" | "no" | "unsure";
  hasElectricalPlans: "" | "yes" | "no" | "unsure";
  hasLightingLayoutAndSchedule:
    | ""
    | "both"
    | "layout_only"
    | "schedule_only"
    | "no"
    | "unsure";
};

const DrawingsIntake = <T extends IntakeShape>({
  intake,
  setIntake,
}: {
  intake: T;
  setIntake: React.Dispatch<React.SetStateAction<T>>;
}) => {
  const set = <K extends keyof IntakeShape>(key: K, value: IntakeShape[K]) => {
    setIntake((prev) => {
      const next: T = { ...prev, [key]: value };
      if (key === "hasFloorPlan" && value !== "yes") {
        (next as IntakeShape).hasElectricalPlans = "";
        (next as IntakeShape).hasLightingLayoutAndSchedule = "";
      }
      if (key === "hasElectricalPlans" && value !== "yes") {
        (next as IntakeShape).hasLightingLayoutAndSchedule = "";
      }
      return next;
    });
  };

  const Chip = ({
    active,
    onClick,
    children,
  }: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 min-h-[36px] text-xs font-light tracking-[0.05em] border transition-all duration-300 ${
        active
          ? "border-white bg-white/[0.06] text-white"
          : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"
      }`}
    >
      {children}
    </button>
  );

  const eyebrow =
    "text-white/45 font-sans font-light text-[10px] tracking-[0.25em] uppercase";
  const label =
    "text-white/75 font-sans font-light text-sm leading-relaxed";

  return (
    <div className="space-y-6 border border-white/10 bg-white/[0.02] p-5 md:p-6">
      <div>
        <p className={eyebrow}>What we have to work with</p>
        <p className={`mt-2 ${label}`}>
          A few quick questions help us point you to the right team and know what to review first.
        </p>
      </div>

      <div>
        <p className={label}>Do you have an existing floor plan for this project?</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {[
            { v: "yes" as const, l: "Yes" },
            { v: "no" as const, l: "Not yet" },
            { v: "unsure" as const, l: "Not sure" },
          ].map((o) => (
            <Chip
              key={o.v}
              active={intake.hasFloorPlan === o.v}
              onClick={() => set("hasFloorPlan", o.v)}
            >
              {o.l}
            </Chip>
          ))}
        </div>
      </div>

      {intake.hasFloorPlan === "yes" && (
        <div>
          <p className={label}>Do you also have electrical plans?</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { v: "yes" as const, l: "Yes" },
              { v: "no" as const, l: "Not yet" },
              { v: "unsure" as const, l: "Not sure" },
            ].map((o) => (
              <Chip
                key={o.v}
                active={intake.hasElectricalPlans === o.v}
                onClick={() => set("hasElectricalPlans", o.v)}
              >
                {o.l}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {intake.hasFloorPlan === "yes" && intake.hasElectricalPlans === "yes" && (
        <div>
          <p className={label}>
            Do the electrical plans include a lighting layout and a lighting fixture schedule?
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { v: "both" as const, l: "Yes, both" },
              { v: "layout_only" as const, l: "Layout only" },
              { v: "schedule_only" as const, l: "Schedule only" },
              { v: "no" as const, l: "Neither yet" },
              { v: "unsure" as const, l: "Not sure" },
            ].map((o) => (
              <Chip
                key={o.v}
                active={intake.hasLightingLayoutAndSchedule === o.v}
                onClick={() => set("hasLightingLayoutAndSchedule", o.v)}
              >
                {o.l}
              </Chip>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

function designAttachHint(i: IntakeShape): string {
  if (
    i.hasFloorPlan === "yes" &&
    i.hasElectricalPlans === "yes" &&
    (i.hasLightingLayoutAndSchedule === "both" ||
      i.hasLightingLayoutAndSchedule === "layout_only" ||
      i.hasLightingLayoutAndSchedule === "schedule_only")
  ) {
    return "Attach your floor plan, electrical plans, and any lighting layout or fixture schedule you have.";
  }
  if (i.hasFloorPlan === "yes" && i.hasElectricalPlans === "yes") {
    return "Attach your floor plan and electrical plans. We will develop the lighting layout and fixture schedule with your team.";
  }
  if (i.hasFloorPlan === "yes") {
    return "Attach your floor plan. We will develop the electrical coordination, lighting layout, and schedule with your team.";
  }
  if (i.hasFloorPlan === "no") {
    return "No drawings needed to start. Share references, site photos, or a short brief and we will take it from there.";
  }
  return "Attach whatever you have. Floor plans, RCPs, fixture schedules, specifications, references, or site photos are all helpful.";
}

function vaRecommendsDesignSwitch(i: IntakeShape): boolean {
  if (i.hasFloorPlan === "" && i.hasElectricalPlans === "" && i.hasLightingLayoutAndSchedule === "") {
    return false;
  }
  if (i.hasFloorPlan !== "" && i.hasFloorPlan !== "yes") return true;
  if (i.hasElectricalPlans === "no") return true;
  if (
    i.hasLightingLayoutAndSchedule === "no" ||
    i.hasLightingLayoutAndSchedule === "layout_only" ||
    i.hasLightingLayoutAndSchedule === "schedule_only"
  ) {
    return true;
  }
  return false;
}

export default SteppedConsultationForm;
